import "./MowerListItem.scss";

import { Box, Icon, Typography } from "@husqvarna/ui-core";
import { calculateRemainingMowerCapacity } from "../../coverageCalculations";
import { ProposalProduct } from "../../models/ProposalProduct";
import { Product } from "../../models/Product";
import Project from "../../models/Project";
import MowerCapacityBadge from "./MowerCapacityBadge";
import MowerIcon from "../../../shared/components/MowerIcon/MowerIcon";
import { useEffect, useRef } from "react";
import { clearAreaHoverValues, setAreaHover } from "../../../shared/store/areaHoverSlice";
import { useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import { UnitSystem } from "../../models/Enums";
import ColorTile from "../../../shared/components/ColorTile/ColorTile";
import { getColorClassForColor } from "../../../shared/constants/proposalProductColors";

export type MowerListItemProps = {
  mower: Product;
  project: Project;
  proposalProduct?: ProposalProduct;
  onClick: (mower: Product, proposalProduct?: ProposalProduct) => void;
}

const MowerListItem = ({
  proposalProduct,
  project,
  mower,
  onClick}: MowerListItemProps) => {
  const mowerItemContainer = useRef<HTMLButtonElement>(null);
  const dispatch = useDispatch();
  const intl = useIntl();
    useEffect(() => {
      if(mowerItemContainer.current && proposalProduct && proposalProduct.areaIds){
        mowerItemContainer.current.onmouseover = (e) => {
          dispatch(setAreaHover([...proposalProduct.areaIds]))
        };
  
        mowerItemContainer.current.onmouseleave = (e) => {
          dispatch(clearAreaHoverValues())
        };
      }
    }, []);

  const calculateRemainingCapacity = () : number => {
    if (project && proposalProduct) {
      const connectedAreas = project?.areas.filter(a => proposalProduct.areaIds.includes(a.id));
      const firstOrDefaultConnection = connectedAreas.find(a => a.proposalProductConnections.find(conn => conn.proposalProduct !== null && conn.proposalProduct.id === proposalProduct.id))?.proposalProductConnections?.[0];
      const possibleToMowOnDifferentHours = firstOrDefaultConnection?.possibleToMowOnDifferentHours ?? true;
      let capacity = calculateRemainingMowerCapacity(proposalProduct.product, proposalProduct.quantity, connectedAreas, possibleToMowOnDifferentHours, proposalProduct);
      
      return capacity;
    }
    return 0;
  }

  const onClickHandler = () => {
    onClick?.call(this, mower, proposalProduct);
  }

  const getAreaNameList = (proposalProduct: ProposalProduct) => {
    const areaNames = proposalProduct?.areaIds.map(id => {
      const area = project.areas.find(area => area.id === id);
      return area ? area.name : '';
    });

    return areaNames.join(', ');
  };
  const getTotalAreaSize = (proposalProduct: ProposalProduct) => {
    const totalAreaSize = proposalProduct?.areaIds.reduce((acc, id) => {
      const area = project.areas.find(area => area.id === id);
      return acc + (area?.size ?? 0);
    }, 0);
    return totalAreaSize;
  }

  const getFormattedAreaSize = (proposalProduct: ProposalProduct) => {
    const areaSize = getTotalAreaSize(proposalProduct);
    const isImperial = project.region.unitSystem === UnitSystem.Imperial;
    const convertedValue = isImperial ? areaSize / 4046.85642 : areaSize;
    const unit = isImperial ? 'acre' : 'meter';
    
    return intl.formatNumber(convertedValue, { 
      style: 'unit', 
      unit: unit, 
      maximumFractionDigits: isImperial ? 2 : 0
    }) + (!isImperial ? '²' : 're');
  }

  return (
    <Box className="mower-item">
      <button className="mower-item-button" type="button" onClick={(e) => onClickHandler()} ref={mowerItemContainer}>
        <Box justifyContent="space-between" alignItems="center" gap={3}>
          <Box flexDirection="row" alignItems="center" gap={3}>
            <MowerIcon product={mower} />
            <Box textAlign="left">
              <Box gap={1}>
                <Typography
                  variant="Microcopy"
                  tag="span"
                  className="mower-item--name"
                  text={mower.name} />
                {proposalProduct && (
                  <Typography
                    variant="Microcopy"
                    className="mower-item--quantity"
                    tag="span"
                    text={` (${proposalProduct.quantity})`} />
                )}
                {proposalProduct?.color && (
                  <ColorTile 
                    color={getColorClassForColor(proposalProduct?.color)} 
                    size="small"
                  />
                )}
              </Box>
              {proposalProduct && (
                <Typography
                  variant="Microcopy"
                  className="mower-item--small-text"
                  color="Grey500"
                  text={getAreaNameList(proposalProduct)} />
              )}
              {proposalProduct && (
                <Typography mt={1}
                  variant="Microcopy"
                  className="mower-item--small-text"
                  color="Grey500"
                  text={getFormattedAreaSize(proposalProduct)} />
              )}
            </Box>
          </Box>
          <Box flexShrink={0} alignItems="center" gap={3}>
            {proposalProduct && (
              <MowerCapacityBadge remainingCapacity={calculateRemainingCapacity()} small />
            )}
            <Icon iconId="angle-right" aria-label="Välj" />
          </Box>
        </Box>
      </button>
      <div className="mower-item-line"></div>
    </Box>
  )
};

export default MowerListItem;